import React, { useState } from 'react';
import {
  Typography,
  Box,
  Button,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { MenuRounded as MenuRoundedIcon } from '@mui/icons-material';
import LogoutNavbar from '../../components/LogoutNavbar';
import GlobalSidebar from '../../components/GlobalSidebar';
import PostLoginFooter from '../../components/PostLoginFooter';
import image from '../../assets/images/University.png';

const Hero = styled(Box)({
  position: 'relative',
  overflow: 'hidden',
  background: '#0b0b14',
});

const Welcome = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [openMobileMenuSignal, setOpenMobileMenuSignal] = useState(0);

  const rolePrefix = location?.pathname?.toLowerCase().startsWith('/clubmember')
    ? '/clubmember'
    : '/student';
  
  const sidebarItems = (() => {
    const profilePath = rolePrefix === '/clubmember' ? '/login/clubmember/home' : '/login/student/home';
    const base = [
      {
        label: 'Profile',
        path: profilePath,
        action: () => navigate(profilePath),
      },
      {
        label: 'My Lost & Found',
        path: `${rolePrefix}/yourlostfound`,
        action: () => navigate(`${rolePrefix}/yourlostfound`),
      },
      {
        label: 'My Resources',
        path: `${rolePrefix}/yourskills`,
        action: () => navigate(`${rolePrefix}/yourskills`),
      },
      {
        label: 'My Doubts',
        path: `${rolePrefix}/yourdoubts`,
        action: () => navigate(`${rolePrefix}/yourdoubts`),
      },
      {
        label: 'Lost & Found',
        path: `${rolePrefix}/lostfound`,
        action: () => navigate(`${rolePrefix}/lostfound`),
      },
      {
        label: 'Resources',
        path: `${rolePrefix}/skills`,
        action: () => navigate(`${rolePrefix}/skills`),
      },
      {
        label: 'Doubts',
        path: `${rolePrefix}/doubts`,
        action: () => navigate(`${rolePrefix}/doubts`),
      },
      {
        label: 'Events',
        path: `${rolePrefix}/events`,
        action: () => navigate(`${rolePrefix}/events`),
      },
      {
        label: 'Know Your College',
        path: `${rolePrefix}/know-your-college`,
        action: () => navigate(`${rolePrefix}/know-your-college`),
      },
    ];

    if (rolePrefix === '/clubmember') {
      base.push({
        label: 'Manage Events',
        path: `${rolePrefix}/eventmanagement`,
        action: () => navigate(`${rolePrefix}/eventmanagement`),
      });
    }

    return base;
  })();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Navbar */}
      <Box data-post-login-navbar="true">
        <LogoutNavbar
          sidebarItems={isMobile ? sidebarItems : []}
          openMobileMenuSignal={openMobileMenuSignal}
        />
      </Box>

      <Box sx={{ display: 'flex', flexGrow: 1 }}>
        {/* Sidebar */}
        {!isMobile && <GlobalSidebar />}

        {/* Main Content */}
        <Box component="main" sx={{ flexGrow: 1, width: '100%', overflow: 'hidden' }}>
          <Hero sx={{ minHeight: 'calc(100vh - 70px)' }}>
            <Box
              component="img"
              src={image}
              alt="University of Lucknow"
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                filter: 'brightness(0.55)',
                zIndex: 0,
              }}
            />

            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(to top, rgba(16,16,30,0.92) 0%, rgba(16,16,30,0.35) 55%, rgba(16,16,30,0.15) 100%)',
                zIndex: 1,
              }}
            />

             {/* Welcome Text Section with modern styling */}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                px: { xs: 2.5, sm: 4, md: 6 },
                // keep it visually clear of the bottom-left title block
                pb: { xs: 14, sm: 16, md: 18 },
                pointerEvents: 'none',
              }}
            >
              <Typography
                variant="h1"
                component="h1"
                sx={{
                  fontFamily: '"Great Vibes", cursive',
                  fontSize: { xs: '3.5rem', md: '5.5rem' },
                  fontWeight: 900,
                  background: 'linear-gradient(135deg, #fff 0%, #e0e0e0 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 2,
                  letterSpacing: '2px',
                }}
              >
                WELCOME  TO
              </Typography>
              
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2rem', md: '2.8rem' },
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #ffff46 0%, #ffef12 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 2,
                  fontFamily: '"Great Vibes", cursive',
                }}
              >
                University of Lucknow
              </Typography>
              
              <Typography
                variant="h5"
                sx={{
                  fontSize: { xs: '1.2rem', md: '1.8rem' },
                  color: 'rgba(255,255,255,0.9)',
                  fontWeight: 300,
                  fontStyle: 'italic',
                  fontFamily: '"Great Vibes", cursive',
                  backdropFilter: 'blur(5px)',
                  display: 'inline-block',
                  padding: '8px 24px',
                  borderRadius: '50px',
                  border: '1px solid rgba(255,255,255,0.2)',
                }}
              >
                Where Knowledge Meets Wisdom
              </Typography>

              {/* Mobile-only Explore button (opens sidebar) */}
              <Box
                sx={{
                  display: { xs: 'flex', md: 'none' },
                  mt: { xs: 3, sm: 3 },
                  pointerEvents: 'auto',
                }}
              >
                <Button
                  variant="contained"
                  startIcon={<MenuRoundedIcon />}
                  onClick={() => setOpenMobileMenuSignal((s) => s + 1)}
                  sx={{
                    bgcolor: '#fff',
                    color: '#0b0b14',
                    fontFamily: '"Great Vibes", cursive',
                    textTransform: 'none',
                    borderRadius: 999,
                    px: 3.5,
                    py: 1.1,
                    fontSize: { xs: '1.6rem', sm: '1.9rem' },
                    boxShadow: 'none',
                    '&:hover': {
                      bgcolor: '#fff',
                      boxShadow: 'none',
                    },
                    '& .MuiButton-startIcon': {
                      mr: 1,
                    },
                  }}
                >
                  Explore
                </Button>
              </Box>
            </Box>

            <Box
              sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                px: { xs: 2.5, sm: 4, md: 6 },
                pb: { xs: 5, sm: 7, md: 9 },
                zIndex: 2,
              }}
            >
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 800,
                  letterSpacing: '0.28em',
                  color: 'rgba(255, 215, 128, 0.95)',
                  textTransform: 'uppercase',
                }}
              >
                Est. 1920
              </Typography>

              <Typography
                component="h1"
                sx={{
                  mt: 0.75,
                  fontFamily: 'serif',
                  fontWeight: 800,
                  color: '#fff',
                  lineHeight: 1.05,
                  fontSize: { xs: '2.6rem', sm: '3.4rem', md: '4.6rem' },
                }}
              >
                University of Lucknow
              </Typography>

              <Typography
                sx={{
                  mt: 1.25,
                  fontStyle: 'italic',
                  fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.35rem' },
                  color: 'rgba(255, 255, 255, 0.78)',
                }}
              >
                Gyan Ka Dwar — Gateway of Knowledge
              </Typography>
            </Box>
          </Hero>
        </Box>
      </Box>

      {/* Footer */}
      <PostLoginFooter />
    </Box>
  );
};

export default Welcome;